import { useEffect, useRef } from "react";
import { MapContainer, TileLayer, Circle, Marker, Popup, Polygon, CircleMarker, useMapEvents, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet-draw";

// Fix default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Blue dot icon for user/device markers — larger with glow for visibility
const userIcon = L.divIcon({
  className: "",
  html: `<div style="width:18px;height:18px;background:#3b82f6;border:3px solid white;border-radius:50%;box-shadow:0 0 8px 2px rgba(59,130,246,0.6),0 2px 4px rgba(0,0,0,0.3);position:relative;z-index:600"></div>`,
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

function ClickHandler({ onClick }) {
  useMapEvents({ click: (e) => onClick(e.latlng) });
  return null;
}

function FitBounds({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (!bounds || bounds.length === 0) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      if (cancelled) return;
      try {
        const pane = map.getPane("mapPane");
        if (pane) {
          map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14, animate: false });
        }
      } catch (e) {}
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [bounds, map]);
  return null;
}

function PolygonDrawer({ onPolygonComplete }) {
  const map = useMap();
  const drawRef = useRef(null);
  const drawnRef = useRef(null);

  useEffect(() => {
    if (!map || drawRef.current) return;
    try {
      const drawnItems = new L.FeatureGroup();
      map.addLayer(drawnItems);
      drawnRef.current = drawnItems;
      const drawControl = new L.Control.Draw({
        draw: {
          polygon: { allowIntersection: false, showArea: true, shapeOptions: { color: "#3b82f6" } },
          polyline: false, rectangle: false, circle: false, marker: false, circlemarker: false,
        },
        edit: { featureGroup: drawnItems },
      });
      map.addControl(drawControl);
      drawRef.current = drawControl;
      const handler = (e) => {
        drawnItems.addLayer(e.layer);
        onPolygonComplete(e.layer.getLatLngs()[0].map(ll => ({ lat: ll.lat, lon: ll.lng })));
      };
      map.on(L.Draw.Event.CREATED, handler);
      return () => {
        try {
          map.off(L.Draw.Event.CREATED, handler);
          if (drawRef.current) map.removeControl(drawRef.current);
          if (drawnRef.current) map.removeLayer(drawnRef.current);
        } catch (e) {}
        drawRef.current = null;
        drawnRef.current = null;
      };
    } catch (e) {}
  }, [map, onPolygonComplete]);

  return null;
}

export function GeofenceMap({
  geofences = [],
  selectedFence = null,
  onMapClick = null,
  clickMarker = null,
  enablePolygonDraw = false,
  onPolygonComplete = null,
  deviceLocations = [],
  locationGroups = [],
  locationRadius = 0,
  height = "400px",
}) {
  const allPoints = [];
  geofences.forEach(g => allPoints.push([g.lat, g.lon]));
  locationGroups.forEach(lg => lg.locations?.forEach(l => allPoints.push([l.lat, l.lon])));
  deviceLocations.forEach(d => { if (d.lat && d.lon) allPoints.push([d.lat, d.lon]); });
  if (clickMarker) allPoints.push([clickMarker.lat, clickMarker.lng || clickMarker.lon]);

  const mapKey = `map-${geofences.length}-${deviceLocations.length}-${locationGroups.length}-${enablePolygonDraw}`;

  return (
    <div style={{ height, width: "100%", position: "relative", zIndex: 0 }} data-testid="geofence-map">
      <MapContainer key={mapKey} center={[20, 0]} zoom={2} style={{ height: "100%", width: "100%", borderRadius: "8px" }} scrollWheelZoom={true} zoomAnimation={false} fadeAnimation={false} markerZoomAnimation={false}>
        <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

        {allPoints.length > 0 && <FitBounds bounds={allPoints} />}
        {onMapClick && <ClickHandler onClick={onMapClick} />}
        {enablePolygonDraw && onPolygonComplete && <PolygonDrawer onPolygonComplete={onPolygonComplete} />}

        {clickMarker && (
          <Marker position={[clickMarker.lat, clickMarker.lng || clickMarker.lon]}>
            <Popup>New geofence<br />{clickMarker.lat.toFixed(4)}, {(clickMarker.lng || clickMarker.lon).toFixed(4)}</Popup>
          </Marker>
        )}

        {/* Geofences — circles */}
        {geofences.map(g => (
          <Circle key={g.id} center={[g.lat, g.lon]} radius={g.radius_m || 500}
            pathOptions={{ color: selectedFence?.id === g.id ? "#ef4444" : "#3b82f6", fillColor: selectedFence?.id === g.id ? "#ef444440" : "#3b82f640", fillOpacity: 0.3, weight: 2 }}>
            <Popup><strong>{g.name}</strong><br />{g.lat}, {g.lon}<br />Radius: {g.radius_m}m</Popup>
          </Circle>
        ))}
        {geofences.map(g => (
          <Marker key={`gm-${g.id}`} position={[g.lat, g.lon]}><Popup><strong>{g.name}</strong></Popup></Marker>
        ))}

        {/* Polygon geofences */}
        {geofences.filter(g => g.polygon?.length >= 3).map(g => (
          <Polygon key={`poly-${g.id}`} positions={g.polygon.map(p => [p.lat, p.lon])}
            pathOptions={{ color: selectedFence?.id === g.id ? "#ef4444" : "#8b5cf6", fillOpacity: 0.2 }}>
            <Popup><strong>{g.name}</strong> (polygon)</Popup>
          </Polygon>
        ))}

        {/* Device locations — blue dots with clustering (shows total on zoom out) */}
        {deviceLocations.length > 0 && (
          <MarkerClusterGroup chunkedLoading maxClusterRadius={60}
            iconCreateFunction={(cluster) => L.divIcon({
              className: "",
              html: `<div style="background:#3b82f6;color:white;border-radius:50%;width:36px;height:36px;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;border:3px solid white;box-shadow:0 2px 8px rgba(59,130,246,0.5)">${cluster.getChildCount()}</div>`,
              iconSize: [36, 36],
              iconAnchor: [18, 18],
            })}>
            {deviceLocations.map((d, i) => (
              d.lat && d.lon ? (
                <Marker key={`dev-${i}`} position={[d.lat, d.lon]} icon={userIcon}>
                  <Popup><strong>User</strong><br />ID: {d.device_id?.slice(0, 20)}<br />{d.count ? `Visits: ${d.count}` : ""}{d.os ? ` · ${d.os}` : ""}</Popup>
                </Marker>
              ) : null
            ))}
          </MarkerClusterGroup>
        )}

        {/* Location groups — orange pin + count badge + highlighted radius */}
        {locationGroups.map(lg =>
          (lg.locations || []).map((loc, i) => {
            const count = loc.count || loc.matched_devices || loc.unique_devices || 0;
            const r = locationRadius || lg.radius || 0;
            return (
              <span key={`lgw-${lg.id}-${i}`}>
                {r > 0 && (
                  <Circle center={[loc.lat, loc.lon]} radius={r}
                    pathOptions={{ color: "#f97316", fillColor: "#f97316", fillOpacity: 0.12, weight: 2 }} />
                )}
                <Marker position={[loc.lat, loc.lon]}
                  icon={L.divIcon({
                    className: "",
                    html: `<div style="position:relative;display:flex;flex-direction:column;align-items:center">
                      <div style="background:#1e1e1e;color:white;padding:2px 8px;border-radius:10px;font-size:12px;font-weight:700;white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,0.35);margin-bottom:4px;min-width:28px;text-align:center">${count}</div>
                      <div style="width:14px;height:14px;background:#f97316;border:2px solid white;border-radius:50%;box-shadow:0 1px 3px rgba(0,0,0,0.3)"></div>
                    </div>`,
                    iconSize: [40, 36], iconAnchor: [20, 36],
                  })}>
                  <Popup><strong>{loc.name || lg.name}</strong><br />{loc.lat}, {loc.lon}<br />{count > 0 && `Devices: ${count}`}{r > 0 && ` · Radius: ${r}m`}</Popup>
                </Marker>
              </span>
            );
          })
        )}
      </MapContainer>
    </div>
  );
}
